<template>
    <section>
        <SideBar />
        <TopBar />
        <div class="content">
            <div class="mb-9">
                <div class="row justify-content-between align-items-end g-3 mb-5">
                    <div class="col-12 col-sm-auto col-xl-10">
                        <h2 class="mb-0">Contact Image</h2>
                        <p class="text-muted mb-0 small">Imagen que aparece en la página de Contacto del sitio público.</p>
                    </div>
                </div>

                <div class="card mb-4">
                    <div class="card-header py-2 d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">Imagen actual (sitio público)</h5>
                        <span v-if="info.updated_at && uploadedLocally" class="small text-muted">Reemplazada local: {{ info.updated_at }}</span>
                    </div>
                    <div class="card-body text-center">
                        <img :src="previewSrc"
                             alt="Contact image"
                             style="max-width:100%;max-height:420px;object-fit:contain;background:#f4f4f4;border-radius:6px" />
                        <dl class="row small text-muted mt-3 mb-0 text-start">
                            <dt class="col-sm-3">Path</dt>
                            <dd class="col-sm-9 text-break">{{ info.path }}</dd>
                            <dt class="col-sm-3">Dimensiones</dt>
                            <dd class="col-sm-9">
                                <span v-if="info.width && info.height">{{ info.width }} × {{ info.height }} px</span>
                                <span v-else>—</span>
                            </dd>
                            <dt class="col-sm-3">Tamaño</dt>
                            <dd class="col-sm-9">{{ formatSize(info.size_bytes) }}</dd>
                        </dl>
                    </div>
                </div>

                <div
                    class="dropzone p-5 text-center"
                    :class="{ 'dropzone--over': dragActive, 'dropzone--busy': uploading }"
                    @click="!uploading && $refs.fileInput.click()"
                    @dragover.prevent="dragActive = true"
                    @dragleave.prevent="dragActive = false"
                    @drop.prevent="onDrop"
                    role="button"
                    tabindex="0"
                    @keydown.enter.prevent="!uploading && $refs.fileInput.click()"
                >
                    <CloudUploadOutlineIcon :size="48" class="text-muted mb-2" />
                    <p v-if="!uploading" class="mb-1">
                        <strong>Arrastra una imagen aquí</strong> o haz click para reemplazar
                    </p>
                    <p v-else class="mb-1">
                        <strong>Subiendo…</strong>
                    </p>
                    <p class="text-muted small mb-0">JPG / PNG / WEBP · máx 5MB · se convierte a JPG automáticamente</p>
                    <input
                        ref="fileInput"
                        type="file"
                        class="d-none"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        @change="onFilePicked"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import SideBar from '@/components/SideBarNav.vue'
import TopBar from '@/components/TopBarNav.vue'
import axios from 'axios'
import CloudUploadOutlineIcon from 'vue-material-design-icons/CloudUploadOutline.vue'

const MAX_BYTES = 5_000_000
const ALLOWED = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp']

export default {
    name: 'ContactImageView',
    components: { SideBar, TopBar, CloudUploadOutlineIcon },
    data() {
        return {
            // The public site marinasamont.com always reads from this URL.
            publicImageUrl: 'https://api.marinasamont.com/uploads/featured/contacto.jpg',
            info: { path: '', exists: false, updated_at: null, width: null, height: null, size_bytes: null },
            dragActive: false,
            uploading: false,
            // Once the admin uploads, we switch the preview to show the local copy.
            uploadedLocally: false,
            // Cache-buster so the same URL refetches after upload.
            cacheBust: Date.now()
        }
    },
    computed: {
        previewSrc() {
            // After a local upload, show the local file with a cache buster.
            // Otherwise show what's currently live on marinasamont.com.
            if (this.uploadedLocally) {
                return this.$url + '/' + this.info.path + '?v=' + this.cacheBust
            }
            return this.publicImageUrl + '?v=' + this.cacheBust
        }
    },
    created() {
        this.load()
    },
    methods: {
        formatSize(n) {
            if (n === null || n === undefined) return '—'
            const v = Number(n)
            if (!isFinite(v) || v <= 0) return '—'
            if (v < 1024) return v + ' B'
            if (v < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB'
            return (v / (1024 * 1024)).toFixed(2) + ' MB'
        },
        load() {
            axios.get(this.$url + '/contact-image', {
                headers: { 'Authorization': this.$store.state.token }
            }).then((res) => {
                this.info = res.data
                this.cacheBust = Date.now()
            }).catch(() => { /* keep defaults */ })
        },
        onFilePicked(event) {
            const file = (event.target.files || [])[0]
            event.target.value = ''
            if (file) this.upload(file)
        },
        onDrop(event) {
            this.dragActive = false
            const file = (event.dataTransfer?.files || [])[0]
            if (file) this.upload(file)
        },
        async upload(file) {
            if (this.uploading) return
            if (!ALLOWED.includes(file.type)) {
                this.$notify({ group: 'iasc', title: 'Archivo rechazado', text: 'Tipo no permitido', type: 'danger' })
                return
            }
            if (file.size > MAX_BYTES) {
                this.$notify({ group: 'iasc', title: 'Archivo rechazado', text: 'Supera 5MB', type: 'danger' })
                return
            }

            this.uploading = true
            try {
                const fm = new FormData()
                fm.append('foto', file)
                const res = await axios.post(this.$url + '/contact-image', fm, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': this.$store.state.token
                    }
                })
                this.info = {
                    path: res.data.path,
                    exists: true,
                    updated_at: res.data.updated_at,
                    width: res.data.width,
                    height: res.data.height,
                    size_bytes: res.data.size_bytes
                }
                this.uploadedLocally = true
                this.cacheBust = Date.now()
                this.$notify({ group: 'iasc', title: 'Listo', text: 'Imagen de contacto actualizada', type: 'success' })
            } catch (err) {
                const msg = err?.response?.data?.error || 'No se pudo subir la imagen'
                this.$notify({ group: 'iasc', title: 'Error', text: msg, type: 'danger' })
            } finally {
                this.uploading = false
            }
        }
    }
}
</script>

<style scoped>
.dropzone {
    border: 2px dashed #c8c8c8;
    border-radius: 12px;
    background: #fafafa;
    transition: background-color .15s ease, border-color .15s ease;
    cursor: pointer;
}
.dropzone:hover,
.dropzone:focus-visible {
    border-color: #6c757d;
    background: #f1f1f1;
    outline: none;
}
.dropzone--over {
    border-color: #0d6efd;
    background: #e7f1ff;
}
.dropzone--busy {
    cursor: progress;
    opacity: 0.8;
}
</style>
